<template>
  <el-dialog
    v-loading="loading"
    title="导入检查内容"
    :visible.sync="dialogVisible"
    width="600px"
    :close-on-click-modal="false"
    append-to-body
    @close="closeDialog"
  >
    <el-form
      ref="importForm"
      class="import-form"
      :model="importForm"
      label-position="right"
      label-width="120px"
    >
      <el-form-item label="获取模板">
        <el-button
          type="success"
          icon="el-icon-download"
          plain
          @click="downloadTemplate"
        >
          下载导入模板
        </el-button>
      </el-form-item>

      <el-form-item label="上传文件">
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx"
          @change="handleFileChange"
        >
      </el-form-item>

      <el-form-item label="备注">
        <div class="import-tips">
          <p>1、请严格按照模板文件进行导入，如果您未下载模板文件，请点击【下载导入模板】。</p>
          <p>2、只能上传.xlsx文件。</p>
          <p>3、文件大小不能超过10M。</p>
          <p class="warning-text">4、导入数据未做重复校验，多次导入会产生多条相同检查内容，如需大批量调整需先删除后导入。</p>
        </div>
      </el-form-item>

      <el-form-item label="导入结果" v-if="importFlag">
        <div class="import-result">
          <p>
            <strong>导入结果：</strong>
          </p>
          <p class="result-summary">
            您总共导入{{ importResult.total }}条记录，成功{{ importResult.successNum }}条，失败{{ importResult.failNum }}条。
          </p>
          <div v-if="importResult.failNum > 0" class="error-list">
            <strong>其中错误信息如下:</strong>
            <ul>
              <li v-for="(item, index) in importResult.errMsgs" :key="index" class="error-item">
                {{ item }}
              </li>
            </ul>
          </div>
        </div>
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button type="primary" icon="el-icon-upload2" @click="handleImport" :loading="loading">
        导入
      </el-button>
      <el-button icon="el-icon-close" @click="closeDialog">
        关闭
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
// 服务
import { checkContentService } from './checkContent.service';
// 工具函数
import { downloadFromApiBlobResponse } from '@/features/common/utils/downloadFileUtil.js';

export default {
  name: 'ImportCheckContentDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      importForm: {},
      importResult: {
        total: 0,
        failNum: 0,
        successNum: 0,
        errMsgs: []
      },
      importFlag: false,
      selectedFile: null
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      }
    }
  },
  methods: {
    /**
     * 下载导入模板
     */
    downloadTemplate() {
      this.loading = true;
      checkContentService.downloadTemplate().then(res => {
        downloadFromApiBlobResponse(res, '检查内容导入模板.xlsx');
      }).finally(() => {
        this.loading = false;
      });
    },

    /**
     * 文件选择变化
     */
    handleFileChange(e) {
      const files = e.target.files;
      if (files && files.length > 0) {
        this.selectedFile = files[0];
      }
    },

    /**
     * 执行导入
     */
    handleImport() {
      if (!this.selectedFile) {
        this.$message.warning('请先选择要导入的文件');
        return;
      }

      // 校验文件大小
      if (this.selectedFile.size / 1024 / 1024 > 10) {
        this.$message.warning('导入文件过大，请控制在10M以内');
        return;
      }

      // 校验文件格式
      if (!this.selectedFile.name.endsWith('.xlsx')) {
        this.$message.warning('请上传.xlsx格式的文件');
        return;
      }

      this.loading = true;
      const formData = new FormData();
      formData.append('file', this.selectedFile);

      checkContentService.importCheckContent(formData).then(res => {
        this.importFlag = true;
        this.importResult = {
          total: res.data.total || 0,
          failNum: res.data.failNum || 0,
          successNum: res.data.successNum || 0,
          errMsgs: []
        };
        // 处理错误信息
        if (res.data.failNum > 0 && res.data.importExcelErrors) {
          this.importResult.errMsgs = res.data.importExcelErrors.map(
            err => `第${err.id}行出现错误：${err.desc}`
          );
        }
        // 通知父组件刷新数据
        this.$emit('success');
      }).finally(() => {
        this.loading = false;
      });
    },

    /**
     * 关闭弹窗
     */
    closeDialog() {
      this.dialogVisible = false;
      this.resetForm();
    },

    /**
     * 重置表单
     */
    resetForm() {
      this.selectedFile = null;
      this.importFlag = false;
      this.importResult = {
        total: 0,
        failNum: 0,
        successNum: 0,
        errMsgs: []
      };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    }
  }
};
</script>

<style scoped lang="scss">
.import-form {
  padding: 10px 10px 0;

  ::v-deep .el-form-item {
    margin-bottom: 25px;
  }

  .import-tips {
    font-size: 13px;
    color: #606266;
    line-height: 2;
    background-color: #f8f9fa;
    padding: 12px 15px;
    border-radius: 4px;
    border: 1px solid #ebeef5;

    p {
      margin-bottom: 4px;
      &:last-child {
        margin-bottom: 0;
      }
    }

    .warning-text {
      color: #e6a23c;
      font-weight: 500;
    }
  }

  .import-result {
    .result-summary {
      color: #409eff;
      margin: 5px 0 15px;
    }

    .error-list {
      ul {
        margin: 10px 0;
        padding-left: 20px;
      }

      .error-item {
        color: #f56c6c;
        margin-bottom: 5px;
      }
    }
  }
}
</style>
